import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {useNavigate } from "react-router-dom"; // Add this import
import {
  RiAddLine,
  RiEditLine,
  RiDeleteBinLine,
  RiSaveLine,
  RiGroupLine,
  RiCheckboxCircleLine,
  RiUserStarLine,
  RiGlobalLine,
  RiAwardLine,
  RiMoneyDollarCircleLine,
  RiCheckLine,
  RiCloseLine,
  RiStarLine,
  RiImageLine,
  RiDraggable,
  RiEyeLine,
  RiEyeOffLine,
  RiUploadLine,
  RiDownloadLine,
  RiCalendarEventLine,
  RiCalendarLine,
  RiTimeLine,
  RiMapPinLine,
  RiSpeakerLine,
  RiFilePdfLine,
  RiLoader4Line,
  RiHotelLine,
  RiBuildingLine,
} from "react-icons/ri";
import toast from "react-hot-toast";
import axios from "axios";
import { API_BASE } from "../config";

axios.defaults.baseURL = API_BASE;

/* ─────────────────────────────────────────────────────────────
   COLOR PALETTE
───────────────────────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F7FF",
  softBlue: "#EBF1FF",
  primary: "#1A56DB",
  primaryDark: "#1240A8",
  primaryLight: "#3B82F6",
  navy: "#0F2557",
  navyDeep: "#091A3E",
  accent: "#60A5FA",
  accentGold: "#F59E0B",
  text: "#0F1F3D",
  textMid: "#374966",
  textMuted: "#6B7FA3",
  border: "#D6E4FF",
  borderLight: "#EBF1FF",
  shadow: "0 4px 24px rgba(26,86,219,0.08)",
  shadowMd: "0 8px 40px rgba(26,86,219,0.12)",
  shadowLg: "0 20px 60px rgba(26,86,219,0.16)",
  gradPrimary: "linear-gradient(135deg, #1A56DB 0%, #3B82F6 100%)",
  gradNavy: "linear-gradient(135deg, #0F2557 0%, #1A56DB 100%)",
  gradSoft: "linear-gradient(135deg, #EBF1FF 0%, #F4F7FF 100%)",
  gradGold: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
};

/* ─────────────────────────────────────────────
   SPONSORS TAB COMPONENT (self-contained)
───────────────────────────────────────────── */


/* ─────────────────────────────────────────────
   PUBLICATIONS / INDEXING TAB (FIXED)
───────────────────────────────────────────── */
const PublicationsTab = ({ token }) => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    order: 0,
    active: true,
    url: "",
  });
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get("/api/publications", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.success && Array.isArray(data.data)) {
        const mapped = data.data.map((p) => ({
          _id: p._id,
          name: p.title || p.name || "",
          logoUrl: p.coverImage || p.logoUrl || "",
          url: p.pdfUrl || p.url || "",
          order: p.order || 0,
          active: p.isActive !== undefined ? p.isActive : (p.active ?? true),
        }));
        setPublications(mapped);
      } else {
        setPublications([]);
      }
    } catch {
      toast.error("Failed to load publications");
      setPublications([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: "", order: 0, active: true, url: "" });
    setEditing(null);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Publication name is required");

    const payload = {
      title: form.name.trim(),
      abstract: form.name.trim(),
      authors: [{ name: "Admin", affiliation: "", email: "", orcidId: "" }],
      keywords: [],
      journalName: "",
      volume: "",
      issue: "",
      pages: "",
      doi: "",
      pdfUrl: form.url || "",
      coverImage: "",
      category: "research",
      isFeatured: false,
      isActive: form.active,
      order: form.order,
    };

    try {
      setLoading(true);
      const cfg = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };

      if (editing) {
        await axios.put(`/api/publications/${editing._id}`, payload, cfg);
        toast.success("Publication updated");
      } else {
        await axios.post("/api/publications", payload, cfg);
        toast.success("Publication added");
      }

      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (pub) => {
    try {
      await axios.patch(
        `/api/publications/${pub._id}/toggle-active`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Publication ${pub.active ? "deactivated" : "activated"}`);
      fetchAll();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const deletePublication = async (id) => {
    if (!window.confirm("Delete this publication?")) return;
    try {
      await axios.delete(`/api/publications/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Publication deleted");
      fetchAll();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const startEdit = (pub) => {
    setEditing(pub);
    setForm({
      name: pub.name,
      order: pub.order ?? 0,
      active: pub.active,
      url: pub.url || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const safePublications = Array.isArray(publications) ? publications : [];
  const activePubs = safePublications
    .filter((p) => p && p.active === true)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const inactivePubs = safePublications.filter((p) => p && p.active === false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: T.white,
        padding: "32px",
        borderRadius: "16px",
        boxShadow: T.shadowMd,
        border: `1px solid ${T.borderLight}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
        <h2 style={{ fontSize: "24px", fontWeight: "bold", color: T.navy }}>
          Manage Publications / Indexing
        </h2>
        <span style={{ fontSize: "12px", color: T.textMuted, background: T.softBlue, padding: "4px 12px", borderRadius: "999px" }}>
          {activePubs.length} active · {inactivePubs.length} hidden
        </span>
      </div>
      <p style={{ color: T.textMuted, marginBottom: "32px", fontSize: "14px" }}>
        These logos appear in the Indexing section. Upload logos of Google Scholar, Scopus, Crossref, etc.
      </p>

      {/* Form */}
      <div style={{ background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "24px", marginBottom: "40px" }}>
        <h3 style={{ fontSize: "18px", fontWeight: "600", color: T.navy, marginBottom: "20px" }}>
          {editing ? `Editing: ${editing.name}` : "Add New Indexing Logo"}
        </h3>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>Name *</label>
            <input
              type="text"
              placeholder="e.g. Google Scholar"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={{ width: "100%", background: T.white, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text }}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>Display Order</label>
            <input
              type="number"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
              style={{ width: "100%", background: T.white, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text }}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>Website URL (optional)</label>
            <input
              type="url"
              placeholder="https://scholar.google.com"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              style={{ width: "100%", background: T.white, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text }}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              style={{ width: "18px", height: "18px", accentColor: T.primary }}
            />
            <label style={{ color: T.textMid, fontSize: "13px" }}>Show on Indexing Page</label>
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
          <button
            onClick={save}
            disabled={loading}
            style={{ padding: "10px 24px", background: T.gradPrimary, color: T.white, border: "none", borderRadius: "8px", display: "flex", alignItems: "center", gap: "8px", cursor: loading ? "not-allowed" : "pointer", fontWeight: 600, opacity: loading ? 0.7 : 1 }}
          >
            💾 {loading ? "Saving..." : editing ? "Update" : "Add Publication"}
          </button>
          {editing && (
            <button
              onClick={resetForm}
              style={{ padding: "10px 24px", background: "#E5E7EB", color: T.textMid, border: "none", borderRadius: "8px", cursor: "pointer" }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Active */}
      <h3 style={{ fontSize: "18px", fontWeight: "600", color: T.navy, marginBottom: "16px" }}>
        Active Publications ({activePubs.length})
      </h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px", marginBottom: "40px" }}>
        {activePubs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "32px", color: T.textMuted, gridColumn: "span 3" }}>
            {loading ? "Loading..." : "No active publications yet."}
          </div>
        ) : (
          activePubs.map((pub) => (
            <div key={pub._id} style={{ background: T.offWhite, padding: "16px", borderRadius: "12px", border: `1px solid ${T.border}`, textAlign: "center" }}>
              <div style={{ height: "64px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "12px", background: T.white, borderRadius: "8px" }}>
                {pub.logoUrl ? (
                  <img src={pub.logoUrl} alt={pub.name} style={{ maxHeight: "48px", maxWidth: "100%", objectFit: "contain" }} />
                ) : (
                  <span style={{ fontSize: "28px" }}>📄</span>
                )}
              </div>
              <p style={{ color: T.text, fontWeight: 500, fontSize: "14px", marginBottom: "4px" }}>{pub.name}</p>
              {pub.url && (
                <a href={pub.url} target="_blank" rel="noopener noreferrer" style={{ color: T.primary, fontSize: "11px", marginBottom: "12px", wordBreak: "break-all", display: "block", textDecoration: "none" }}>
                  {pub.url}
                </a>
              )}
              <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "10px" }}>
                <button onClick={(e) => { e.stopPropagation(); toggleActive(pub); }} style={{ padding: "6px 10px", background: "#FEF3C7", color: "#92400E", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>
                  🙈 Hide
                </button>
                <button onClick={(e) => { e.stopPropagation(); startEdit(pub); }} style={{ padding: "6px 10px", background: T.softBlue, color: T.primary, border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>
                  ✏️ Edit
                </button>
                <button onClick={(e) => { e.stopPropagation(); deletePublication(pub._id); }} style={{ padding: "6px 10px", background: "#FEE2E2", color: "#EF4444", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>
                  🗑
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Hidden */}
      {inactivePubs.length > 0 && (
        <>
          <h3 style={{ fontSize: "18px", fontWeight: "600", color: T.navy, marginBottom: "16px" }}>
            Hidden Publications ({inactivePubs.length})
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", opacity: 0.7 }}>
            {inactivePubs.map((pub) => (
              <div key={pub._id} style={{ display: "flex", alignItems: "center", gap: "16px", background: T.offWhite, padding: "12px 16px", borderRadius: "12px" }}>
                <span style={{ fontSize: "24px" }}>📄</span>
                <div style={{ flex: 1 }}>
                  <p style={{ color: T.text, fontWeight: 500 }}>{pub.name}</p>
                  {pub.url && <p style={{ color: T.textMuted, fontSize: "12px" }}>{pub.url}</p>}
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button onClick={(e) => { e.stopPropagation(); toggleActive(pub); }} style={{ padding: "6px 12px", background: "#E8F5E9", color: "#4CAF50", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>
                    👁 Show
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); startEdit(pub); }} style={{ padding: "6px 12px", background: T.softBlue, color: T.primary, border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>
                    ✏️ Edit
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); deletePublication(pub._id); }} style={{ padding: "6px 12px", background: "#FEE2E2", color: "#EF4444", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </motion.div>
  );
};

/* ─────────────────────────────────────────────
   UPDATED ACCOMMODATIONS TAB
───────────────────────────────────────────── */


/* ─────────────────────────────────────────────
   UPDATED ANNOUNCEMENTS TAB
───────────────────────────────────────────── */
const AnnouncementsTab = ({ token }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    message: "",
    isActive: true,
    priority: "medium",
    backgroundColor: "#d4a853",
    textColor: "#ffffff",
    link: "",
    expiresAt: "",
  });
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

const fetchAnnouncements = async () => {
  try {
    setLoading(true);
    // Check if token exists before making the request
    const token = localStorage.getItem("token");
    if (!token) {
      setAnnouncements([]);
      setLoading(false);
      return;
    }
    
    const { data } = await axios.get("/api/announcements", {
      headers: { Authorization: `Bearer ${token}` }
    });
    setAnnouncements(data.announcements || []);
  } catch (error) {
    console.error("Failed to load announcements:", error);
    // Don't show toast for 401 errors, just set empty array
    if (error.response?.status !== 401) {
      toast.error("Failed to load announcements");
    }
    setAnnouncements([]);
  } finally {
    setLoading(false);
  }
};

  const resetForm = () => {
    setForm({
      message: "",
      isActive: true,
      priority: "medium",
      backgroundColor: "#d4a853",
      textColor: "#ffffff",
      link: "",
      expiresAt: "",
    });
    setEditing(null);
  };

  const save = async () => {
    if (!form.message.trim()) {
      toast.error("Message is required");
      return;
    }

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = {
        ...form,
        expiresAt: form.expiresAt ? new Date(form.expiresAt) : null,
      };

      if (editing) {
        await axios.put(`/api/announcements/${editing._id}`, payload, config);
        toast.success("Announcement updated");
      } else {
        await axios.post("/api/announcements", payload, config);
        toast.success("Announcement created");
      }

      resetForm();
      fetchAnnouncements();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await axios.delete(`/api/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Announcement deleted");
      fetchAnnouncements();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleActive = async (announcement) => {
    try {
      await axios.patch(
        `/api/announcements/${announcement._id}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Announcement ${!announcement.isActive ? "activated" : "deactivated"}`);
      fetchAnnouncements();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const startEdit = (ann) => {
    setEditing(ann);
    setForm({
      message: ann.message,
      isActive: ann.isActive,
      priority: ann.priority || "medium",
      backgroundColor: ann.backgroundColor || "#d4a853",
      textColor: ann.textColor || "#ffffff",
      link: ann.link || "",
      expiresAt: ann.expiresAt?.slice(0, 16) || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: T.white,
        padding: "32px",
        borderRadius: "16px",
        boxShadow: T.shadowMd,
        border: `1px solid ${T.borderLight}`,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h2 style={{ fontSize: "24px", fontWeight: "bold", color: T.navy }}>
          Manage Announcements
        </h2>
        <span style={{ fontSize: "12px", color: T.textMuted, background: T.softBlue, padding: "4px 12px", borderRadius: "999px" }}>
          {announcements.filter(a => a.isActive).length} active · {announcements.filter(a => !a.isActive).length} hidden
        </span>
      </div>
      <p style={{ color: T.textMuted, marginBottom: "32px", fontSize: "14px" }}>
        Create and manage breaking news banners that appear at the top of the website.
      </p>

      <div
        style={{
          background: T.offWhite,
          border: `1px solid ${T.border}`,
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "40px",
        }}
      >
        <h3 style={{ fontSize: "18px", fontWeight: "semibold", color: T.navy, marginBottom: "20px" }}>
          {editing ? `Editing Announcement` : "Create New Announcement"}
        </h3>

        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Message *
            </label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows="3"
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
              placeholder="Enter announcement message..."
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "16px" }}>
            <div>
              <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
                Priority
              </label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                style={{
                  width: "100%",
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  padding: "12px",
                  color: T.text,
                }}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div>
              <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
                Background Color
              </label>
              <input
                type="color"
                value={form.backgroundColor}
                onChange={(e) => setForm({ ...form, backgroundColor: e.target.value })}
                style={{
                  width: "100%",
                  height: "48px",
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
                Text Color
              </label>
              <input
                type="color"
                value={form.textColor}
                onChange={(e) => setForm({ ...form, textColor: e.target.value })}
                style={{
                  width: "100%",
                  height: "48px",
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
                Link URL (optional)
              </label>
              <input
                type="url"
                placeholder="https://example.com"
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                style={{
                  width: "100%",
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  padding: "12px",
                  color: T.text,
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
                Expires At
              </label>
              <input
                type="datetime-local"
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                style={{
                  width: "100%",
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  padding: "12px",
                  color: T.text,
                }}
              />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              style={{ width: "18px", height: "18px", accentColor: T.primary }}
            />
            <label style={{ color: T.textMid, fontSize: "13px", cursor: "pointer" }}>
              Show on website (active)
            </label>
          </div>

          <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
            <button
              onClick={save}
              disabled={loading}
              style={{
                padding: "10px 24px",
                background: T.gradPrimary,
                color: T.white,
                border: "none",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              <RiSaveLine size={18} />
              {loading ? "Saving..." : editing ? "Update Announcement" : "Create Announcement"}
            </button>
            {editing && (
              <button
                onClick={resetForm}
                style={{
                  padding: "10px 24px",
                  background: "#E5E7EB",
                  color: T.textMid,
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      <h3 style={{ fontSize: "18px", fontWeight: "semibold", color: T.navy, marginBottom: "16px" }}>
        📢 All Announcements ({announcements.length})
      </h3>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {announcements.map((ann) => (
          <div
            key={ann._id}
            style={{
              background: T.offWhite,
              borderRadius: "12px",
              padding: "16px",
              borderLeft: `4px solid ${ann.isActive ? T.primary : "#E5E7EB"}`,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "8px" }}>
                  <p style={{ color: T.text, fontSize: "14px", fontWeight: 500 }}>{ann.message}</p>
                  <span style={{
                    fontSize: "11px",
                    padding: "2px 8px",
                    borderRadius: "20px",
                    background: ann.isActive ? "#E8F5E9" : "#FEE2E2",
                    color: ann.isActive ? "#4CAF50" : "#EF4444",
                  }}>
                    {ann.isActive ? "Active" : "Inactive"}
                  </span>
                  <span style={{
                    fontSize: "11px",
                    padding: "2px 8px",
                    borderRadius: "20px",
                    background: ann.priority === "high" ? "#FEE2E2" : ann.priority === "medium" ? "#FEF3C7" : "#E8F5E9",
                    color: ann.priority === "high" ? "#EF4444" : ann.priority === "medium" ? "#F59E0B" : "#4CAF50",
                  }}>
                    {ann.priority}
                  </span>
                </div>
                {ann.expiresAt && (
                  <p style={{ color: T.textMuted, fontSize: "12px" }}>
                    Expires: {new Date(ann.expiresAt).toLocaleDateString()}
                  </p>
                )}
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => toggleActive(ann)}
                  style={{
                    padding: "8px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    background: ann.isActive ? "#FEF3C7" : "#E8F5E9",
                    color: ann.isActive ? T.accentGold : "#4CAF50",
                    border: "none",
                  }}
                  title={ann.isActive ? "Deactivate" : "Activate"}
                >
                  {ann.isActive ? <RiEyeOffLine size={16} /> : <RiEyeLine size={16} />}
                </button>
                <button
                  onClick={() => startEdit(ann)}
                  style={{ padding: "8px", background: T.softBlue, color: T.primary, border: "none", borderRadius: "8px", cursor: "pointer" }}
                >
                  <RiEditLine size={16} />
                </button>
                <button
                  onClick={() => deleteAnnouncement(ann._id)}
                  style={{ padding: "8px", background: "#FEE2E2", color: "#EF4444", border: "none", borderRadius: "8px", cursor: "pointer" }}
                >
                  <RiDeleteBinLine size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {announcements.length === 0 && !loading && (
          <div style={{ textAlign: "center", padding: "32px", color: T.textMuted }}>
            No announcements yet. Create your first announcement above.
          </div>
        )}
      </div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────
   UPDATED SPEAKERS TAB
───────────────────────────────────────────── */
const SpeakersTab = ({ token }) => {
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    bio: "",
    bulletPoints: [],
    imageUrl: "",
    type: "individual",
    email: "",
    linkedin: "",
    twitter: "",
  });
  const [bulletPointInput, setBulletPointInput] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [editing, setEditing] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchSpeakers();
  }, []);

  const fetchSpeakers = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/speakers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSpeakers(Array.isArray(data) ? data : data.speakers || []);
    } catch (error) {
      toast.error("Failed to load speakers");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    setUploadingImage(true);
    const imageFormData = new FormData();
    imageFormData.append("image", file);
    
    try {
      const uploadRes = await axios.post(`${API_BASE}/api/upload`, imageFormData, {
        headers: { 
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`  // Add token here
        },
      });
      const imageUrl = uploadRes.data.url || uploadRes.data.imageUrl;
      setForm(prev => ({ ...prev, imageUrl }));
      setImagePreview(imageUrl);
      toast.success("Image uploaded");
      return imageUrl;
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.message || "Failed to upload image");
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageUrlChange = (url) => {
    setForm({ ...form, imageUrl: url });
    setImagePreview(url);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    
    await handleImageUpload(file);
  };

  const resetForm = () => {
    setForm({
      name: "",
      company: "",
      bio: "",
      bulletPoints: [],
      imageUrl: "",
      type: "individual",
      email: "",
      linkedin: "",
      twitter: "",
    });
    setBulletPointInput("");
    setImagePreview(null);
    setEditing(null);
  };

  const addBulletPoint = () => {
    if (bulletPointInput.trim()) {
      setForm({
        ...form,
        bulletPoints: [...form.bulletPoints, bulletPointInput.trim()],
      });
      setBulletPointInput("");
    }
  };

  const removeBulletPoint = (index) => {
    setForm({
      ...form,
      bulletPoints: form.bulletPoints.filter((_, i) => i !== index),
    });
  };

  const save = async () => {
    if (!form.name || !form.bio) {
      toast.error("Name and Bio are required");
      return;
    }

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { ...form };

      if (editing) {
        await axios.put(`${API_BASE}/api/speakers/${editing._id}`, payload, config);
        toast.success("Speaker updated");
      } else {
        await axios.post(`${API_BASE}/api/speakers`, payload, config);
        toast.success("Speaker added");
      }

      resetForm();
      fetchSpeakers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteSpeaker = async (id) => {
    if (!window.confirm("Delete this speaker?")) return;
    try {
      await axios.delete(`${API_BASE}/api/speakers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted");
      fetchSpeakers();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const startEdit = (speaker) => {
    setEditing(speaker);
    setForm({
      name: speaker.name || "",
      company: speaker.company || "",
      bio: speaker.bio || "",
      bulletPoints: speaker.bulletPoints || [],
      imageUrl: speaker.imageUrl || "",
      type: speaker.type || "individual",
      email: speaker.email || "",
      linkedin: speaker.linkedin || "",
      twitter: speaker.twitter || "",
    });
    setImagePreview(speaker.imageUrl || null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "keynote": return "🎤 Keynote";
      case "plenary": return "📢 Plenary";
      default: return "👤 Individual";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "keynote": return T.primary;
      case "plenary": return T.accentGold;
      default: return T.primaryLight;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: T.white,
        padding: "32px",
        borderRadius: "16px",
        boxShadow: T.shadowMd,
        border: `1px solid ${T.borderLight}`,
      }}
    >
      <h2 style={{ fontSize: "24px", fontWeight: "bold", color: T.navy, marginBottom: "24px" }}>
        Manage Speakers
      </h2>
      <p style={{ color: T.textMuted, marginBottom: "32px", fontSize: "14px" }}>
        Add and manage conference speakers with their bios, images, and key highlights.
      </p>

      <div
        style={{
          background: T.offWhite,
          border: `1px solid ${T.border}`,
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "40px",
        }}
      >
        <h3 style={{ fontSize: "18px", fontWeight: "semibold", color: T.navy, marginBottom: "20px" }}>
          {editing ? `Editing: ${editing.name}` : "Add New Speaker"}
        </h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Speaker Type *
            </label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            >
              <option value="keynote">🎤 Keynote Speaker</option>
              <option value="plenary">📢 Plenary Speaker</option>
              <option value="individual">👤 Individual Speaker</option>
            </select>
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Full Name *
            </label>
            <input
              type="text"
              placeholder="e.g., Dr. Sarah Johnson"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Company / Affiliation
            </label>
            <input
              type="text"
              placeholder="e.g., Harvard Medical School"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Profile Image
            </label>
            
            {/* Image Preview */}
            {imagePreview && (
              <div style={{ marginBottom: "12px" }}>
                <img
                  src={imagePreview}
                  alt="preview"
                  style={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: `2px solid ${T.primary}`,
                  }}
                  onError={() => setImagePreview(null)}
                />
              </div>
            )}
            
            {/* Option 1: Upload from file */}
            <div style={{ marginBottom: "12px" }}>
              <input
                id="speaker-image-upload"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
                disabled={uploadingImage}
              />
              <label
                htmlFor="speaker-image-upload"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                  background: T.softBlue,
                  borderRadius: "8px",
                  fontSize: "13px",
                  color: T.primary,
                  cursor: uploadingImage ? "not-allowed" : "pointer",
                  border: `1px solid ${T.border}`,
                  opacity: uploadingImage ? 0.6 : 1,
                }}
              >
                <RiUploadLine size={16} />
                {uploadingImage ? "Uploading..." : "Upload Image from Device"}
              </label>
            </div>
            
            {/* Option 2: Paste URL */}
            <div>
              <p style={{ fontSize: "11px", color: T.textMuted, marginBottom: "6px" }}>
                OR paste image URL:
              </p>
              <input
                type="text"
                placeholder="https://example.com/speaker-image.jpg"
                value={form.imageUrl}
                onChange={(e) => handleImageUrlChange(e.target.value)}
                style={{
                  width: "100%",
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  padding: "10px",
                  color: T.text,
                  fontSize: "13px",
                }}
              />
            </div>
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Biography *
            </label>
            <textarea
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              rows="4"
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
              placeholder="Write a professional biography..."
            />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Key Highlights / Achievements (Pointwise)
            </label>
            <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
              <input
                type="text"
                value={bulletPointInput}
                onChange={(e) => setBulletPointInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && addBulletPoint()}
                placeholder="e.g., Nobel Prize Winner 2023"
                style={{
                  flex: 1,
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: "8px",
                  padding: "10px",
                  color: T.text,
                }}
              />
              <button
                onClick={addBulletPoint}
                style={{
                  padding: "8px 16px",
                  background: T.softBlue,
                  color: T.primary,
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                <RiAddLine size={20} />
              </button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {form.bulletPoints.map((bp, idx) => (
                <span
                  key={idx}
                  style={{
                    background: T.white,
                    padding: "4px 12px",
                    borderRadius: "999px",
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <RiCheckLine size={14} style={{ color: "#4CAF50" }} />
                  {bp}
                  <button
                    onClick={() => removeBulletPoint(idx)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#EF4444",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Email
            </label>
            <input
              type="email"
              placeholder="speaker@example.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              LinkedIn URL
            </label>
            <input
              type="text"
              placeholder="https://linkedin.com/in/username"
              value={form.linkedin}
              onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Twitter Handle
            </label>
            <input
              type="text"
              placeholder="@username"
              value={form.twitter}
              onChange={(e) => setForm({ ...form, twitter: e.target.value })}
              style={{
                width: "100%",
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: "8px",
                padding: "12px",
                color: T.text,
              }}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
          <button
            onClick={save}
            disabled={loading || uploadingImage}
            style={{
              padding: "10px 24px",
              background: T.gradPrimary,
              color: T.white,
              border: "none",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              fontWeight: 600,
              opacity: loading || uploadingImage ? 0.6 : 1,
            }}
          >
            <RiSaveLine size={18} />
            {loading ? "Saving..." : uploadingImage ? "Uploading..." : editing ? "Update Speaker" : "Add Speaker"}
          </button>
          {editing && (
            <button
              onClick={resetForm}
              style={{
                padding: "10px 24px",
                background: "#E5E7EB",
                color: T.textMid,
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <h3 style={{ fontSize: "18px", fontWeight: "semibold", color: T.navy, marginBottom: "16px" }}>
        Current Speakers ({speakers.length})
      </h3>
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {speakers.map((speaker) => (
          <div
            key={speaker._id}
            style={{
              background: T.offWhite,
              padding: "16px",
              borderRadius: "12px",
              display: "flex",
              gap: "16px",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {speaker.imageUrl && (
              <img
                src={speaker.imageUrl}
                alt={speaker.name}
                style={{
                  width: 70,
                  height: 70,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "4px" }}>
                <h4 style={{ color: T.text, fontWeight: "bold", fontSize: "16px" }}>{speaker.name}</h4>
                <span
                  style={{
                    fontSize: "10px",
                    padding: "2px 10px",
                    borderRadius: "20px",
                    background: getTypeColor(speaker.type) + "15",
                    color: getTypeColor(speaker.type),
                  }}
                >
                  {getTypeLabel(speaker.type)}
                </span>
              </div>
              {speaker.company && (
                <p style={{ color: T.textMuted, fontSize: "12px", marginBottom: "6px" }}>
                  {speaker.company}
                </p>
              )}
              <p style={{ color: T.text, fontSize: "12px", marginBottom: "8px" }}>{speaker.bio?.substring(0, 120)}...</p>
              {speaker.bulletPoints && speaker.bulletPoints.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }}>
                  {speaker.bulletPoints.slice(0, 2).map((bp, i) => (
                    <span key={i} style={{ fontSize: "10px", background: T.white, padding: "2px 8px", borderRadius: "4px" }}>
                      ✓ {bp}
                    </span>
                  ))}
                  {speaker.bulletPoints.length > 2 && (
                    <span style={{ fontSize: "10px", color: T.textMuted }}>+{speaker.bulletPoints.length - 2} more</span>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <button
                onClick={() => startEdit(speaker)}
                style={{ padding: "8px", background: T.softBlue, color: T.primary, border: "none", borderRadius: "8px", cursor: "pointer" }}
              >
                <RiEditLine />
              </button>
              <button
                onClick={() => deleteSpeaker(speaker._id)}
                style={{ padding: "8px", background: "#FEE2E2", color: "#EF4444", border: "none", borderRadius: "8px", cursor: "pointer" }}
              >
                <RiDeleteBinLine />
              </button>
            </div>
          </div>
        ))}
        {speakers.length === 0 && !loading && (
          <div style={{ textAlign: "center", padding: "48px", color: T.textMuted, background: T.offWhite, borderRadius: "12px" }}>
            No speakers yet. Add your first speaker above.
          </div>
        )}
      </div>
    </motion.div>
  );
};

/* ─────────────────────────────────────────────
   UPDATED EVENTS TAB WITH VIEW BUTTON
───────────────────────────────────────────── */

const EventsTab = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    date: "",
    location: "",
    venue: "",
    description: "",
    speakers: [],
    tracks: [],
    type: "conference",
    price: "",
    status: "upcoming",
    capacity: "",
    featured: false,
    hideFromList: false,
    startTime: "",
    endTime: "",
  });
  const [speakerInput, setSpeakerInput] = useState("");
  const [trackInput, setTrackInput] = useState("");
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  // ✅ Fetch ALL events (including hidden) for admin panel
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/events?admin=true`);
      if (data.success) setEvents(data.events);
    } catch {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const resetForm = () => {
    setForm({
      title: "",
      subtitle: "",
      date: "",
      location: "",
      venue: "",
      description: "",
      speakers: [],
      tracks: [],
      type: "conference",
      price: "",
      status: "upcoming",
      capacity: "",
      featured: false,
      hideFromList: false,
      startTime: "",
      endTime: "",
    });
    setSpeakerInput("");
    setTrackInput("");
    setEditing(null);
  };

  const addSpeaker = () => {
    if (speakerInput.trim()) {
      setForm({ ...form, speakers: [...form.speakers, speakerInput.trim()] });
      setSpeakerInput("");
    }
  };

  const removeSpeaker = (index) => {
    setForm({ ...form, speakers: form.speakers.filter((_, i) => i !== index) });
  };

  const addTrack = () => {
    if (trackInput.trim()) {
      setForm({ ...form, tracks: [...form.tracks, trackInput.trim()] });
      setTrackInput("");
    }
  };

  const removeTrack = (index) => {
    setForm({ ...form, tracks: form.tracks.filter((_, i) => i !== index) });
  };

  const getEventSlug = (event) => {
    if (event.slug) return event.slug;
    return event.title
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/--+/g, "-")
      .trim();
  };

  const viewEvent = (e, event) => {
    e.stopPropagation();
    const slug = getEventSlug(event);
    window.open(`/event/${slug}`, "_blank");
  };

  const editInSummit = (e, event) => {
    e.stopPropagation();
    navigate(`/dashboard/summits?eventId=${event._id}`);
  };

  const save = async () => {
    if (!form.title || !form.date || !form.location) {
      toast.error("Title, Date and Location are required");
      return;
    }

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editing) {
        await axios.put(`${API_BASE}/api/events/${editing._id}`, form, config);
        toast.success("Event updated");
      } else {
        const response = await axios.post(`${API_BASE}/api/events`, form, config);
        toast.success(`Event created! View at: /event/${response.data.event.slug}`);
      }

      resetForm();
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API_BASE}/api/events/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Deleted");
      fetchEvents();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API_BASE}/api/events/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Marked ${status}`);
      fetchEvents();
    } catch {
      toast.error("Failed to update");
    }
  };

  const toggleHideFromList = async (id, hideFromList) => {
    try {
      await axios.patch(
        `${API_BASE}/api/events/${id}/hide`,
        { hideFromList: !hideFromList },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Event ${!hideFromList ? "hidden from" : "shown on"} list`);
      fetchEvents();
    } catch {
      toast.error("Failed to update");
    }
  };

  const startEdit = (e, event) => {
    e.stopPropagation();
    setEditing(event);
    setForm({
      title: event.title,
      subtitle: event.subtitle || "",
      date: event.date,
      location: event.location,
      venue: event.venue || "",
      description: event.description || "",
      speakers: event.speakers || [],
      tracks: event.tracks || [],
      type: event.type || "conference",
      price: event.price || "",
      status: event.status || "upcoming",
      capacity: event.capacity || "",
      featured: event.featured || false,
      hideFromList: event.hideFromList || false,
      startTime: event.startTime || "",
      endTime: event.endTime || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const visibleEvents = events.filter((e) => !e.hideFromList);
  const hiddenEvents = events.filter((e) => e.hideFromList);

  const inputStyle = {
    width: "100%",
    background: T.white,
    border: `1px solid ${T.border}`,
    borderRadius: "8px",
    padding: "12px",
    color: T.text,
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: T.white,
        padding: "32px",
        borderRadius: "16px",
        boxShadow: T.shadowMd,
        border: `1px solid ${T.borderLight}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <h2 style={{ fontSize: "24px", fontWeight: "bold", color: T.navy }}>
          Manage Events
        </h2>
        <span
          style={{
            fontSize: "12px",
            color: T.textMuted,
            background: T.softBlue,
            padding: "4px 12px",
            borderRadius: "999px",
          }}
        >
          {visibleEvents.length} visible · {hiddenEvents.length} hidden
        </span>
      </div>
      <p style={{ color: T.textMuted, marginBottom: "32px", fontSize: "14px" }}>
        Create and manage conference events. Click <strong>"Edit in Summit"</strong> on any event to manage its hero slides, speakers, schedule, and all other content.
      </p>

      {/* Form */}
      <div
        style={{
          background: T.offWhite,
          border: `1px solid ${T.border}`,
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "40px",
        }}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "semibold",
            color: T.navy,
            marginBottom: "20px",
          }}
        >
          {editing ? `Editing: ${editing.title}` : "Create New Event"}
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "16px",
          }}
        >
          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Event Title *
            </label>
            <input
              type="text"
              placeholder="e.g., International Conference on AI"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Subtitle
            </label>
            <input
              type="text"
              placeholder="e.g., Shaping the Future"
              value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Date *
            </label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Start Time
            </label>
            <input
              type="time"
              value={form.startTime}
              onChange={(e) => setForm({ ...form, startTime: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              End Time
            </label>
            <input
              type="time"
              value={form.endTime}
              onChange={(e) => setForm({ ...form, endTime: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Location *
            </label>
            <input
              type="text"
              placeholder="e.g., Boston, MA"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Venue
            </label>
            <input
              type="text"
              placeholder="e.g., Convention Center"
              value={form.venue}
              onChange={(e) => setForm({ ...form, venue: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Price
            </label>
            <input
              type="text"
              placeholder="e.g., $299 or Free"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Capacity
            </label>
            <input
              type="text"
              placeholder="e.g., 500 attendees"
              value={form.capacity}
              onChange={(e) => setForm({ ...form, capacity: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Event Type
            </label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              style={inputStyle}
            >
              <option value="conference">Conference</option>
              <option value="workshop">Workshop</option>
              <option value="seminar">Seminar</option>
              <option value="webinar">Webinar</option>
              <option value="meetup">Meetup</option>
            </select>
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Status
            </label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              style={inputStyle}
            >
              <option value="upcoming">Upcoming</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                style={{ width: "18px", height: "18px", accentColor: T.accentGold }}
              />
              <span style={{ color: T.textMid }}>⭐ Featured Event</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.hideFromList}
                onChange={(e) => setForm({ ...form, hideFromList: e.target.checked })}
                style={{ width: "18px", height: "18px", accentColor: T.primary }}
              />
              <span style={{ color: T.textMid }}>🙈 Hide from Events List</span>
            </label>
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows="4"
              placeholder="Event description..."
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Speakers
            </label>
            <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
              <input
                type="text"
                value={speakerInput}
                onChange={(e) => setSpeakerInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && addSpeaker()}
                placeholder="Add a speaker name"
                style={{ flex: 1, background: T.white, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "10px", color: T.text }}
              />
              <button
                onClick={addSpeaker}
                style={{ padding: "8px 16px", background: T.softBlue, color: T.primary, border: "none", borderRadius: "8px", cursor: "pointer" }}
              >
                + Add
              </button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {form.speakers.map((s, idx) => (
                <span
                  key={idx}
                  style={{ background: T.white, padding: "4px 12px", borderRadius: "999px", fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" }}
                >
                  🎤 {s}
                  <button
                    onClick={() => removeSpeaker(idx)}
                    style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: "16px", lineHeight: 1 }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <label style={{ display: "block", color: T.textMid, fontSize: "13px", marginBottom: "4px" }}>
              Tracks / Topics
            </label>
            <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
              <input
                type="text"
                value={trackInput}
                onChange={(e) => setTrackInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && addTrack()}
                placeholder="Add a track"
                style={{ flex: 1, background: T.white, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "10px", color: T.text }}
              />
              <button
                onClick={addTrack}
                style={{ padding: "8px 16px", background: T.softBlue, color: T.primary, border: "none", borderRadius: "8px", cursor: "pointer" }}
              >
                + Add
              </button>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {form.tracks.map((t, idx) => (
                <span
                  key={idx}
                  style={{ background: T.white, padding: "4px 12px", borderRadius: "999px", fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" }}
                >
                  📌 {t}
                  <button
                    onClick={() => removeTrack(idx)}
                    style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: "16px", lineHeight: 1 }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
          <button
            onClick={save}
            disabled={loading}
            style={{
              padding: "10px 24px",
              background: T.gradPrimary,
              color: T.white,
              border: "none",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: 600,
              opacity: loading ? 0.7 : 1,
            }}
          >
            💾 {loading ? "Saving..." : editing ? "Update Event" : "Create Event"}
          </button>
          {editing && (
            <button
              onClick={resetForm}
              style={{ padding: "10px 24px", background: "#E5E7EB", color: T.textMid, border: "none", borderRadius: "8px", cursor: "pointer" }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Visible Events */}
      <h3 style={{ fontSize: "18px", fontWeight: "semibold", color: T.navy, marginBottom: "16px" }}>
        📅 Visible Events ({visibleEvents.length})
      </h3>
      <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "40px" }}>
        {visibleEvents.length === 0 && (
          <p style={{ color: T.textMuted, fontSize: "14px" }}>No visible events yet.</p>
        )}
        {visibleEvents.map((event) => (
          <div
            key={event._id}
            style={{ background: T.offWhite, padding: "16px", borderRadius: "12px" }}
          >
            <div
              style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}
            >
              <div style={{ flex: 1 }}>
                <h4 style={{ color: T.text, fontWeight: "bold", marginBottom: "4px" }}>{event.title}</h4>
                <p style={{ color: T.textMuted, fontSize: "13px" }}>
                  {event.date} · {event.location}
                </p>
                <a
                  href={`/event/${getEventSlug(event)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: T.primary, fontSize: "12px", marginTop: "4px", display: "inline-block", textDecoration: "none" }}
                >
                  🔗 /event/{getEventSlug(event)}
                </a>
              </div>

              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                {event.status !== "completed" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); updateStatus(event._id, "completed"); }}
                    style={{ padding: "6px 12px", background: "#E8F5E9", color: "#4CAF50", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}
                  >
                    ✓ Mark Completed
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); toggleHideFromList(event._id, event.hideFromList); }}
                  style={{ padding: "6px 12px", background: "#FEF3C7", color: "#92400E", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}
                >
                  🙈 Hide
                </button>

                <button
                  onClick={(e) => viewEvent(e, event)}
                  style={{
                    padding: "6px 14px",
                    background: "#EFF6FF",
                    color: "#1A56DB",
                    border: "1px solid #BFDBFE",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  👁 View
                </button>

                <button
                  onClick={(e) => startEdit(e, event)}
                  style={{ padding: "6px 14px", background: T.softBlue, color: T.primary, border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
                >
                  ✏️ Edit Info
                </button>

                <button
                  onClick={(e) => editInSummit(e, event)}
                  style={{
                    padding: "6px 14px",
                    background: T.gradPrimary,
                    color: T.white,
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: 600,
                  }}
                >
                  🏔️ Edit in Summit
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); deleteEvent(event._id); }}
                  style={{ padding: "6px 14px", background: "#FEE2E2", color: "#EF4444", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
                >
                  🗑 Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Hidden Events */}
      {hiddenEvents.length > 0 && (
        <>
          <h3 style={{ fontSize: "18px", fontWeight: "semibold", color: T.navy, marginBottom: "16px" }}>
            🙈 Hidden Events ({hiddenEvents.length})
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", opacity: 0.7 }}>
            {hiddenEvents.map((event) => (
              <div
                key={event._id}
                style={{ background: T.offWhite, padding: "16px", borderRadius: "12px" }}
              >
                <div
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}
                >
                  <div>
                    <h4 style={{ color: T.text, fontWeight: "bold", marginBottom: "4px" }}>{event.title}</h4>
                    <p style={{ color: T.textMuted, fontSize: "13px" }}>
                      {event.date} · {event.location}
                    </p>
                    <a
                      href={`/event/${getEventSlug(event)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: T.primary, fontSize: "12px", marginTop: "4px", display: "inline-block", textDecoration: "none" }}
                    >
                      🔗 /event/{getEventSlug(event)}
                    </a>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleHideFromList(event._id, event.hideFromList); }}
                      style={{ padding: "6px 12px", background: "#E8F5E9", color: "#4CAF50", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}
                    >
                      👁 Show on List
                    </button>

                    <button
                      onClick={(e) => viewEvent(e, event)}
                      style={{
                        padding: "6px 14px",
                        background: "#EFF6FF",
                        color: "#1A56DB",
                        border: "1px solid #BFDBFE",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: 600,
                      }}
                    >
                      👁 View
                    </button>

                    <button
                      onClick={(e) => startEdit(e, event)}
                      style={{ padding: "6px 14px", background: T.softBlue, color: T.primary, border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
                    >
                      ✏️ Edit Info
                    </button>

                    <button
                      onClick={(e) => editInSummit(e, event)}
                      style={{
                        padding: "6px 14px",
                        background: T.gradPrimary,
                        color: T.white,
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: 600,
                      }}
                    >
                      🏔️ Edit in Summit
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </motion.div>
  );
};
const ContentManagement = () => {
  const [activeTab, setActiveTab] = useState("hero-slides");
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  // ==================== ABSTRACT SUBMISSIONS STATE ====================
  const [abstracts, setAbstracts] = useState([]);
  const [loadingAbstracts, setLoadingAbstracts] = useState(false);
  const [selectedAbstract, setSelectedAbstract] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // ==================== HERO SLIDES STATE ====================
  const [heroSlides, setHeroSlides] = useState([]);
  const [newHeroSlide, setNewHeroSlide] = useState({ title: "", order: 0, active: true });
  const [editingHeroSlide, setEditingHeroSlide] = useState(null);
  const [heroImageFile, setHeroImageFile] = useState(null);

  // ==================== STATS STATE ====================
  const [stats, setStats] = useState({ attendees: "5,000+", speakers: "50+", countries: "30+", awards: "10+" });
  const [tempStats, setTempStats] = useState({ ...stats });

  // ==================== BROCHURE STATE ====================
  const [brochureInfo, setBrochureInfo] = useState({
    title: "Become a Sponsor",
    description: "Partner with us to reach thousands of industry leaders and innovators.",
    buttonText: "Download Brochure",
    isActive: true,
  });
  const [brochureFile, setBrochureFile] = useState(null);
  const [brochureFileName, setBrochureFileName] = useState("");
  const [uploadingBrochure, setUploadingBrochure] = useState(false);
  const [brochurePdfUrl, setBrochurePdfUrl] = useState(null);
  const [editingBrochure, setEditingBrochure] = useState(null);

  // ==================== PRICING STATE ====================
  const [pricingTiers, setPricingTiers] = useState([]);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [newPricingTier, setNewPricingTier] = useState({ 
    title: "", 
    price: 0, 
    priceDisplay: "", 
    featured: false, 
    color: "#06B6D4", 
    features: [], 
    order: 0, 
    isActive: true 
  });
  const [featureInput, setFeatureInput] = useState("");
  const [editingPricing, setEditingPricing] = useState(null);

  const [loading, setLoading] = useState(false);

  // ====================== ABSTRACT FUNCTIONS ======================
  const fetchAbstracts = async () => {
    try {
      setLoadingAbstracts(true);
      const { data } = await axios.get("/api/abstracts", { headers: { Authorization: `Bearer ${token}` } });
      if (data.success) setAbstracts(data.abstracts);
    } catch {
      toast.error("Failed to load abstract submissions");
    } finally {
      setLoadingAbstracts(false);
    }
  };

  const updateAbstractStatus = async (id, status) => {
    try {
      await axios.patch(`/api/abstracts/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Abstract ${status}`);
      fetchAbstracts();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const deleteAbstract = async (id) => {
    if (!window.confirm("Delete this abstract submission?")) return;
    try {
      await axios.delete(`/api/abstracts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Abstract deleted");
      fetchAbstracts();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const downloadAbstractFile = async (id, filename) => {
    try {
      const response = await axios.get(`/api/abstracts/${id}/download`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("File downloaded!");
    } catch {
      toast.error("Failed to download");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved": return { bg: "#E8F5E9", color: "#4CAF50", label: "Approved" };
      case "rejected": return { bg: "#FEE2E2", color: "#EF4444", label: "Rejected" };
      default: return { bg: "#FEF3C7", color: "#F59E0B", label: "Pending" };
    }
  };

  const filteredAbstracts = abstracts.filter((abstract) => {
    const matchesStatus = filterStatus === "all" || abstract.status === filterStatus;
    const matchesSearch = searchTerm === "" ||
      abstract.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      abstract.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      abstract.abstractTitle?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // ====================== HERO SLIDES FUNCTIONS ======================
  const fetchHeroSlides = async () => {
    try {
      const { data } = await axios.get("/api/hero-slides");
      setHeroSlides(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load hero slides");
    }
  };

  const saveHeroSlide = async () => {
    if (!newHeroSlide.title?.trim()) return toast.error("Title is required");
    setLoading(true);
    const formData = new FormData();
    formData.append("title", newHeroSlide.title);
    formData.append("order", newHeroSlide.order);
    formData.append("active", newHeroSlide.active);
    if (heroImageFile) formData.append("image", heroImageFile);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (editingHeroSlide) {
        await axios.put(`/api/hero-slides/${editingHeroSlide._id}`, formData, config);
        toast.success("Slide updated");
      } else {
        await axios.post("/api/hero-slides", formData, config);
        toast.success("Slide created");
      }
      setNewHeroSlide({ title: "", order: 0, active: true });
      setHeroImageFile(null);
      setEditingHeroSlide(null);
      fetchHeroSlides();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const deleteHeroSlide = async (id) => {
    if (!window.confirm("Delete this slide?")) return;
    try {
      await axios.delete(`/api/hero-slides/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted");
      fetchHeroSlides();
    } catch {
      toast.error("Failed to delete");
    }
  };

  // ====================== STATS FUNCTIONS ======================
  const fetchStats = async () => {
    try {
      const { data } = await axios.get("/api/stats");
      if (data.success) {
        setStats(data.stats);
        setTempStats(data.stats);
      }
    } catch {
      toast.error("Failed to load stats");
    }
  };

  const updateStats = async () => {
    setLoading(true);
    try {
      const { data } = await axios.put("/api/stats", tempStats, { headers: { Authorization: `Bearer ${token}` } });
      if (data.success) {
        setStats(tempStats);
        toast.success("Statistics updated!");
      }
    } catch {
      toast.error("Failed to update");
    } finally {
      setLoading(false);
    }
  };

  // ====================== PRICING FUNCTIONS ======================
  const fetchPricingTiers = async () => {
    try {
      setLoadingPricing(true);
      const response = await axios.get("/api/pricing");
      setPricingTiers(response.data.success && Array.isArray(response.data.data) ? response.data.data : []);
    } catch {
      toast.error("Failed to load pricing");
      setPricingTiers([]);
    } finally {
      setLoadingPricing(false);
    }
  };

  const savePricingTier = async () => {
    if (!newPricingTier.title || !newPricingTier.price) return toast.error("Title and Price required");
    if (newPricingTier.features.length === 0) return toast.error("At least one feature required");
    setLoadingPricing(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { ...newPricingTier, priceDisplay: newPricingTier.priceDisplay || `$${newPricingTier.price}` };
      if (editingPricing) {
        await axios.put(`/api/pricing/${editingPricing._id}`, payload, config);
        toast.success("Pricing tier updated");
      } else {
        await axios.post("/api/pricing", payload, config);
        toast.success("Pricing tier created");
      }
      setNewPricingTier({ title: "", price: 0, priceDisplay: "", featured: false, color: "#06B6D4", features: [], order: pricingTiers.length, isActive: true });
      setFeatureInput("");
      setEditingPricing(null);
      fetchPricingTiers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setLoadingPricing(false);
    }
  };

  const deletePricingTier = async (id) => {
    if (!window.confirm("Delete this pricing tier?")) return;
    try {
      await axios.delete(`/api/pricing/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted");
      fetchPricingTiers();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const addFeatureToPricing = () => {
    if (featureInput.trim()) {
      setNewPricingTier({ ...newPricingTier, features: [...newPricingTier.features, featureInput.trim()] });
      setFeatureInput("");
    }
  };

  const removeFeatureFromPricing = (idx) => {
    setNewPricingTier({ ...newPricingTier, features: newPricingTier.features.filter((_, i) => i !== idx) });
  };

  // ====================== BROCHURE FUNCTIONS ======================
  const fetchBrochureInfo = async () => {
    try {
      const { data } = await axios.get("/api/brochure/brochure-info");
      if (data.success) {
        setBrochureInfo({
          title: data.title || "Become a Sponsor",
          description: data.description || "Partner with us to reach thousands of industry leaders and innovators.",
          buttonText: data.buttonText || "Download Brochure",
          isActive: data.isActive !== false,
        });
        setBrochurePdfUrl(data.hasBrochure ? "/uploads/brochures/sponsor-brochure.pdf" : null);
        setBrochureFileName(data.hasBrochure ? "Current Brochure.pdf" : "");
      }
    } catch {
      console.error("Failed to fetch brochure");
    }
  };

  const saveBrochureInfo = async () => {
    if (!brochureInfo.title.trim()) {
      toast.error("Brochure title is required");
      return;
    }

    setUploadingBrochure(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const metadataPayload = {
        title: brochureInfo.title,
        description: brochureInfo.description,
        buttonText: brochureInfo.buttonText,
        isActive: brochureInfo.isActive,
      };

      if (editingBrochure) {
        await axios.put(`/api/brochure/info/${editingBrochure}`, metadataPayload, config);
      } else {
        await axios.post("/api/brochure/info", metadataPayload, config);
      }

      if (brochureFile) {
        const formData = new FormData();
        formData.append("brochure", brochureFile);
        await axios.post("/api/brochure/upload-brochure", formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        toast.success("Brochure PDF uploaded successfully!");
        setBrochureFile(null);
        setBrochureFileName("");
      }

      toast.success("Brochure information saved!");
      fetchBrochureInfo();
      setEditingBrochure(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save brochure");
    } finally {
      setUploadingBrochure(false);
    }
  };

  const handleBrochureFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed!");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }
    setBrochureFile(file);
    setBrochureFileName(file.name);
  };

  const downloadBrochure = async () => {
    try {
      const response = await axios.get("/api/brochure/download-brochure", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", brochureInfo.title.replace(/\s+/g, "-") + ".pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Brochure downloaded!");
    } catch {
      toast.error("Brochure not available");
    }
  };

  const deleteBrochure = async () => {
    if (!window.confirm("Delete the brochure PDF?")) return;
    try {
      await axios.delete("/api/brochure/delete-brochure", { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Brochure PDF deleted");
      fetchBrochureInfo();
    } catch {
      toast.error("Failed to delete brochure");
    }
  };

  // ====================== TABS ======================
  const TABS = [
    { id: "hero-slides", label: "Hero Slides" },
    { id: "announcements", label: "Announcements" },
  
    { id: "events", label: "Events" },
    { id: "stats", label: "Statistics" },
    { id: "pricing", label: "💰 Pricing" },
   
    { id: "abstracts", label: "📝 Abstract Submissions" },

  ];

  // ====================== useEffect ======================
  useEffect(() => {
    if (activeTab === "abstracts") fetchAbstracts();
    if (activeTab === "hero-slides") fetchHeroSlides();
    if (activeTab === "stats") fetchStats();
    if (activeTab === "brochure") fetchBrochureInfo();
    if (activeTab === "pricing") fetchPricingTiers();
  }, [activeTab]);

  return (
    <div style={{ padding: "24px" }}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <h1 style={{ fontSize: "30px", fontWeight: "bold", color: T.navy, marginBottom: "32px" }}>
        Content Management
      </h1>

      <div style={{ display: "flex", gap: "12px", marginBottom: "32px", flexWrap: "wrap" }}>
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            style={{
              padding: "10px 20px",
              borderRadius: "10px",
              fontSize: "14px",
              fontWeight: 500,
              transition: "all 0.2s",
              cursor: "pointer",
              background: activeTab === id ? T.primary : T.white,
              color: activeTab === id ? T.white : T.textMid,
              border: activeTab === id ? "none" : `1px solid ${T.border}`,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === "announcements" && <AnnouncementsTab token={token} />}
      {activeTab === "events" && <EventsTab token={token} />}

      {activeTab === "abstracts" && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: T.white,
            padding: "32px",
            borderRadius: "16px",
            boxShadow: T.shadowMd,
            border: `1px solid ${T.borderLight}`,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", flexWrap: "wrap", gap: "16px" }}>
            <div>
              <h2 style={{ fontSize: "24px", fontWeight: "bold", color: T.navy }}>Abstract Submissions</h2>
              <p style={{ color: T.textMuted, marginTop: "4px" }}>Manage and review all abstract submissions</p>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ padding: "10px 16px", borderRadius: "8px", border: `1px solid ${T.border}`, background: T.white, width: "250px", fontSize: "14px" }}
              />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{ padding: "10px 16px", borderRadius: "8px", border: `1px solid ${T.border}`, background: T.white, fontSize: "14px" }}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "32px" }}>
            <div style={{ background: T.offWhite, padding: "16px", borderRadius: "12px", textAlign: "center" }}>
              <div style={{ fontSize: "28px", fontWeight: "bold", color: T.primary }}>{abstracts.length}</div>
              <div style={{ fontSize: "13px", color: T.textMuted }}>Total</div>
            </div>
            <div style={{ background: T.offWhite, padding: "16px", borderRadius: "12px", textAlign: "center" }}>
              <div style={{ fontSize: "28px", fontWeight: "bold", color: "#F59E0B" }}>{abstracts.filter(a => a.status === "pending").length}</div>
              <div style={{ fontSize: "13px", color: T.textMuted }}>Pending</div>
            </div>
            <div style={{ background: T.offWhite, padding: "16px", borderRadius: "12px", textAlign: "center" }}>
              <div style={{ fontSize: "28px", fontWeight: "bold", color: "#4CAF50" }}>{abstracts.filter(a => a.status === "approved").length}</div>
              <div style={{ fontSize: "13px", color: T.textMuted }}>Approved</div>
            </div>
            <div style={{ background: T.offWhite, padding: "16px", borderRadius: "12px", textAlign: "center" }}>
              <div style={{ fontSize: "28px", fontWeight: "bold", color: "#EF4444" }}>{abstracts.filter(a => a.status === "rejected").length}</div>
              <div style={{ fontSize: "13px", color: T.textMuted }}>Rejected</div>
            </div>
          </div>

          {loadingAbstracts ? (
            <div style={{ textAlign: "center", padding: "48px" }}>
              <div style={{ width: "40px", height: "40px", border: `3px solid ${T.border}`, borderTopColor: T.primary, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto" }} />
            </div>
          ) : filteredAbstracts.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px", color: T.textMuted }}>No abstract submissions found</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {filteredAbstracts.map((abstract) => (
                <div key={abstract._id} style={{ background: T.offWhite, borderRadius: "12px", padding: "20px", border: `1px solid ${T.borderLight}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "12px" }}>
                        <h3 style={{ fontSize: "18px", fontWeight: "semibold", color: T.text }}>{abstract.abstractTitle}</h3>
                        <span style={{ padding: "4px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "600", background: getStatusColor(abstract.status).bg, color: getStatusColor(abstract.status).color }}>
                          {getStatusColor(abstract.status).label}
                        </span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "12px", marginBottom: "12px" }}>
                        <div>
                          <div style={{ fontSize: "12px", color: T.textMuted, marginBottom: "4px" }}>Submitted By</div>
                          <div style={{ fontSize: "14px", color: T.text, fontWeight: 500 }}>{abstract.fullName}</div>
                          <div style={{ fontSize: "12px", color: T.textMuted }}>{abstract.email}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: "12px", color: T.textMuted, marginBottom: "4px" }}>Presentation Type</div>
                          <div style={{ fontSize: "14px", color: T.text }}>{abstract.presentationType === "oral" ? "🎤 Oral" : abstract.presentationType === "poster" ? "📊 Poster" : "Either"}</div>
                        </div>
                      </div>
                      {abstract.supportingFile && (
                        <div style={{ marginTop: "12px", padding: "12px", background: T.white, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div style={{ width: "40px", height: "40px", background: "#FEE2E2", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <span style={{ fontSize: "20px" }}>📄</span>
                            </div>
                            <div>
                              <div style={{ fontSize: "13px", fontWeight: 500, color: T.text }}>{abstract.supportingFile.originalName}</div>
                              <div style={{ fontSize: "11px", color: T.textMuted }}>{(abstract.supportingFile.fileSize / 1024 / 1024).toFixed(2)} MB</div>
                            </div>
                          </div>
                          <button onClick={() => downloadAbstractFile(abstract._id, abstract.supportingFile.originalName)} style={{ padding: "8px 16px", background: T.softBlue, color: T.primary, border: "none", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                            <RiDownloadLine size={16} /> Download
                          </button>
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {abstract.status === "pending" && (
                        <>
                          <button onClick={() => updateAbstractStatus(abstract._id, "approved")} style={{ padding: "8px 16px", background: "#4CAF50", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                            <RiCheckboxCircleLine size={16} /> Approve
                          </button>
                          <button onClick={() => updateAbstractStatus(abstract._id, "rejected")} style={{ padding: "8px 16px", background: "#EF4444", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                            <RiCloseLine size={16} /> Reject
                          </button>
                        </>
                      )}
                      {abstract.status !== "pending" && (
                        <button onClick={() => updateAbstractStatus(abstract._id, "pending")} style={{ padding: "8px 16px", background: "#F59E0B", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                          <RiEditLine size={16} /> Reset
                        </button>
                      )}
                      <button onClick={() => setSelectedAbstract(selectedAbstract === abstract._id ? null : abstract._id)} style={{ padding: "8px 16px", background: T.softBlue, color: T.primary, border: "none", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                        <RiEyeLine size={16} /> {selectedAbstract === abstract._id ? "Hide" : "View Details"}
                      </button>
                      <button onClick={() => deleteAbstract(abstract._id)} style={{ padding: "8px 16px", background: "#FEE2E2", color: "#EF4444", border: "none", borderRadius: "6px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                        <RiDeleteBinLine size={16} /> Delete
                      </button>
                    </div>
                  </div>
                  {selectedAbstract === abstract._id && (
                    <div style={{ marginTop: "20px", paddingTop: "20px", borderTop: `1px solid ${T.border}` }}>
                      <h4 style={{ fontSize: "14px", fontWeight: "semibold", color: T.text, marginBottom: "8px" }}>Additional Information</h4>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "16px" }}>
                        <div><div style={{ fontSize: "12px", color: T.textMuted }}>Affiliation</div><div style={{ fontSize: "13px", color: T.text }}>{abstract.affiliation || "N/A"}</div></div>
                        <div><div style={{ fontSize: "12px", color: T.textMuted }}>Phone</div><div style={{ fontSize: "13px", color: T.text }}>{abstract.phone || "N/A"}</div></div>
                        <div><div style={{ fontSize: "12px", color: T.textMuted }}>Submission ID</div><div style={{ fontSize: "12px", color: T.text, fontFamily: "monospace" }}>{abstract._id}</div></div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

{activeTab === "hero-slides" && (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    style={{ background: T.white, padding: "32px", borderRadius: "16px", boxShadow: T.shadowMd, border: `1px solid ${T.borderLight}` }}
  >
    <h2 style={{ fontSize: "24px", fontWeight: "bold", color: T.navy, marginBottom: "24px" }}>Manage Hero Slides</h2>
    
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px", marginBottom: "32px" }}>
      {/* Basic Info Column */}
      <div>
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px" }}>Slide Title</label>
        <textarea 
          value={newHeroSlide.title} 
          onChange={(e) => setNewHeroSlide({ ...newHeroSlide, title: e.target.value })} 
          rows="3" 
          style={{ width: "100%", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "12px", color: T.text }} 
          placeholder="MEETING 21&#10;Global Leadership&#10;2026" 
        />
        
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px", marginTop: "16px" }}>Subtitle / Badge Text</label>
        <input 
          type="text" 
          value={newHeroSlide.subtitle || ""} 
          onChange={(e) => setNewHeroSlide({ ...newHeroSlide, subtitle: e.target.value })} 
          style={{ width: "100%", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text }} 
          placeholder="Global Leadership Summit" 
        />
        
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px", marginTop: "16px" }}>CTA Button Text</label>
        <input 
          type="text" 
          value={newHeroSlide.ctaText || "Upcoming Events"} 
          onChange={(e) => setNewHeroSlide({ ...newHeroSlide, ctaText: e.target.value })} 
          style={{ width: "100%", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text }} 
        />
        
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px", marginTop: "16px" }}>Display Order</label>
        <input 
          type="number" 
          value={newHeroSlide.order} 
          onChange={(e) => setNewHeroSlide({ ...newHeroSlide, order: parseInt(e.target.value) || 0 })} 
          style={{ width: "100%", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text }} 
        />
        
        <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "16px" }}>
          <input 
            type="checkbox" 
            checked={newHeroSlide.active} 
            onChange={(e) => setNewHeroSlide({ ...newHeroSlide, active: e.target.checked })} 
            style={{ width: "18px", height: "18px", accentColor: T.primary }} 
          />
          <span style={{ color: T.textMid }}>Active on Website</span>
        </label>
        
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px", marginTop: "16px" }}>Hero Image</label>
        <input 
          type="file" 
          accept="image/*" 
          onChange={(e) => setHeroImageFile(e.target.files[0])} 
          style={{ width: "100%" }} 
        />
      </div>
      
      {/* Title Styling Column */}
      <div>
        <h3 style={{ fontSize: "16px", fontWeight: "bold", color: T.navy, marginBottom: "16px", borderBottom: `2px solid ${T.primary}`, paddingBottom: "8px" }}>Title Styling</h3>
        
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px" }}>Font Family</label>
        <select 
          value={newHeroSlide.textStyles?.titleFontFamily || "'Playfair Display', serif"} 
          onChange={(e) => setNewHeroSlide({ 
            ...newHeroSlide, 
            textStyles: { ...newHeroSlide.textStyles, titleFontFamily: e.target.value } 
          })}
          style={{ width: "100%", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text, marginBottom: "12px" }}
        >
          <option value="'Playfair Display', serif">Playfair Display (Serif)</option>
          <option value="'DM Sans', sans-serif">DM Sans (Sans-Serif)</option>
          <option value="'Montserrat', sans-serif">Montserrat</option>
          <option value="'Poppins', sans-serif">Poppins</option>
          <option value="'Inter', sans-serif">Inter</option>
        </select>
        
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px" }}>Font Size</label>
        <input 
          type="text" 
          value={newHeroSlide.textStyles?.titleFontSize || "clamp(40px, 6vw, 82px)"} 
          onChange={(e) => setNewHeroSlide({ 
            ...newHeroSlide, 
            textStyles: { ...newHeroSlide.textStyles, titleFontSize: e.target.value } 
          })}
          style={{ width: "100%", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text, marginBottom: "12px" }} 
          placeholder="clamp(40px, 6vw, 82px)"
        />
        
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px" }}>Font Weight</label>
        <select 
          value={newHeroSlide.textStyles?.titleFontWeight || "700"} 
          onChange={(e) => setNewHeroSlide({ 
            ...newHeroSlide, 
            textStyles: { ...newHeroSlide.textStyles, titleFontWeight: e.target.value } 
          })}
          style={{ width: "100%", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text, marginBottom: "12px" }}
        >
          <option value="400">400 (Normal)</option>
          <option value="500">500 (Medium)</option>
          <option value="600">600 (Semi-Bold)</option>
          <option value="700">700 (Bold)</option>
          <option value="800">800 (Extra Bold)</option>
          <option value="900">900 (Black)</option>
        </select>
        
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px" }}>Line Height</label>
        <input 
          type="text" 
          value={newHeroSlide.textStyles?.titleLineHeight || "1.06"} 
          onChange={(e) => setNewHeroSlide({ 
            ...newHeroSlide, 
            textStyles: { ...newHeroSlide.textStyles, titleLineHeight: e.target.value } 
          })}
          style={{ width: "100%", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text, marginBottom: "12px" }} 
        />
        
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px" }}>Letter Spacing</label>
        <input 
          type="text" 
          value={newHeroSlide.textStyles?.titleLetterSpacing || "-0.02em"} 
          onChange={(e) => setNewHeroSlide({ 
            ...newHeroSlide, 
            textStyles: { ...newHeroSlide.textStyles, titleLetterSpacing: e.target.value } 
          })}
          style={{ width: "100%", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text, marginBottom: "12px" }} 
        />
        
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px" }}>Title Color</label>
        <input 
          type="color" 
          value={newHeroSlide.textStyles?.titleColor || "#FFFFFF"} 
          onChange={(e) => setNewHeroSlide({ 
            ...newHeroSlide, 
            textStyles: { ...newHeroSlide.textStyles, titleColor: e.target.value } 
          })}
          style={{ width: "100%", height: "40px", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "4px", marginBottom: "12px" }} 
        />
      </div>
      
      {/* Subtitle/Badge Styling Column */}
      <div>
        <h3 style={{ fontSize: "16px", fontWeight: "bold", color: T.navy, marginBottom: "16px", borderBottom: `2px solid ${T.primary}`, paddingBottom: "8px" }}>Subtitle/Badge Styling</h3>
        
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px" }}>Font Family</label>
        <select 
          value={newHeroSlide.textStyles?.subtitleFontFamily || "'DM Sans', sans-serif"} 
          onChange={(e) => setNewHeroSlide({ 
            ...newHeroSlide, 
            textStyles: { ...newHeroSlide.textStyles, subtitleFontFamily: e.target.value } 
          })}
          style={{ width: "100%", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text, marginBottom: "12px" }}
        >
          <option value="'DM Sans', sans-serif">DM Sans</option>
          <option value="'Playfair Display', serif">Playfair Display</option>
          <option value="'Montserrat', sans-serif">Montserrat</option>
          <option value="'Poppins', sans-serif">Poppins</option>
        </select>
        
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px" }}>Font Size</label>
        <input 
          type="text" 
          value={newHeroSlide.textStyles?.subtitleFontSize || "11.5px"} 
          onChange={(e) => setNewHeroSlide({ 
            ...newHeroSlide, 
            textStyles: { ...newHeroSlide.textStyles, subtitleFontSize: e.target.value } 
          })}
          style={{ width: "100%", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text, marginBottom: "12px" }} 
        />
        
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px" }}>Font Weight</label>
        <select 
          value={newHeroSlide.textStyles?.subtitleFontWeight || "700"} 
          onChange={(e) => setNewHeroSlide({ 
            ...newHeroSlide, 
            textStyles: { ...newHeroSlide.textStyles, subtitleFontWeight: e.target.value } 
          })}
          style={{ width: "100%", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text, marginBottom: "12px" }}
        >
          <option value="400">400 (Normal)</option>
          <option value="500">500 (Medium)</option>
          <option value="600">600 (Semi-Bold)</option>
          <option value="700">700 (Bold)</option>
          <option value="800">800 (Extra Bold)</option>
        </select>
        
        <label style={{ display: "block", color: T.textMid, marginBottom: "8px" }}>Subtitle Color</label>
        <input 
          type="color" 
          value={newHeroSlide.textStyles?.subtitleColor || "rgba(255,255,255,0.95)"} 
          onChange={(e) => setNewHeroSlide({ 
            ...newHeroSlide, 
            textStyles: { ...newHeroSlide.textStyles, subtitleColor: e.target.value } 
          })}
          style={{ width: "100%", height: "40px", background: T.offWhite, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "4px", marginBottom: "12px" }} 
        />
      </div>
    </div>
    
    <button 
      onClick={saveHeroSlide} 
      disabled={loading} 
      style={{ padding: "12px 32px", background: T.gradPrimary, color: T.white, border: "none", borderRadius: "12px", display: "flex", alignItems: "center", gap: "12px", cursor: "pointer", fontWeight: 600, marginBottom: "48px" }}
    >
      <RiSaveLine size={20} /> {editingHeroSlide ? "Update Slide" : "Add New Hero Slide"}
    </button>
    
    <h3 style={{ fontSize: "20px", fontWeight: "semibold", color: T.navy, marginBottom: "16px" }}>Current Slides</h3>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))", gap: "16px" }}>
      {heroSlides.map((slide) => (
        <div key={slide._id} style={{ display: "flex", gap: "16px", background: T.offWhite, padding: "16px", borderRadius: "12px" }}>
          <img src={slide.image} alt="" style={{ width: "96px", height: "80px", objectFit: "cover", borderRadius: "8px" }} />
          <div style={{ flex: 1 }}>
            <p style={{ color: T.text, whiteSpace: "pre-line", fontSize: "13px", fontWeight: "bold" }}>{slide.title}</p>
            {slide.subtitle && <p style={{ color: T.textMuted, fontSize: "11px", marginTop: "4px" }}>{slide.subtitle}</p>}
            <p style={{ color: T.textMuted, fontSize: "10px", marginTop: "4px" }}>Order: {slide.order} · {slide.active ? "✅ Active" : "❌ Inactive"}</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <button onClick={() => { setNewHeroSlide(slide); setEditingHeroSlide(slide); }} style={{ padding: "8px", background: T.softBlue, color: T.primary, border: "none", borderRadius: "6px", cursor: "pointer" }}><RiEditLine /></button>
            <button onClick={() => deleteHeroSlide(slide._id)} style={{ padding: "8px", background: "#FEE2E2", color: "#EF4444", border: "none", borderRadius: "6px", cursor: "pointer" }}><RiDeleteBinLine /></button>
          </div>
        </div>
      ))}
    </div>
  </motion.div>
)}

      {activeTab === "stats" && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ background: T.white, padding: "32px", borderRadius: "16px", boxShadow: T.shadowMd, border: `1px solid ${T.borderLight}` }}
        >
          <h2 style={{ fontSize: "24px", fontWeight: "bold", color: T.navy, marginBottom: "24px" }}>Statistics Management</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "24px", marginBottom: "32px" }}>
            {[
              { key: "attendees", label: "Total Attendees", icon: RiGroupLine },
              { key: "speakers", label: "Expert Speakers", icon: RiUserStarLine },
              { key: "countries", label: "Countries", icon: RiGlobalLine },
              { key: "awards", label: "Awards", icon: RiAwardLine },
            ].map(({ key, label, icon: Icon }) => (
              <div key={key} style={{ background: T.offWhite, padding: "24px", borderRadius: "12px", border: `1px solid ${T.border}` }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                  <div style={{ width: "48px", height: "48px", background: `${T.primary}20`, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon style={{ color: T.primary, fontSize: "24px" }} />
                  </div>
                  <h3 style={{ color: T.text, fontWeight: "semibold" }}>{label}</h3>
                </div>
                <input type="text" value={tempStats[key] || ""} onChange={(e) => setTempStats({ ...tempStats, [key]: e.target.value })} style={{ width: "100%", background: T.white, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text, fontSize: "24px", fontWeight: "bold", textAlign: "center" }} />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: "16px" }}>
            <button onClick={updateStats} disabled={loading} style={{ padding: "10px 24px", background: T.gradPrimary, color: T.white, border: "none", borderRadius: "8px", display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontWeight: 600 }}>
              <RiSaveLine size={18} /> {loading ? "Saving..." : "Save All Statistics"}
            </button>
            <button onClick={() => { setTempStats(stats); fetchStats(); }} style={{ padding: "10px 24px", background: "#E5E7EB", color: T.textMid, border: "none", borderRadius: "8px", cursor: "pointer" }}>Reset</button>
          </div>
        </motion.div>
      )}

      {activeTab === "pricing" && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ background: T.white, padding: "32px", borderRadius: "16px", boxShadow: T.shadowMd, border: `1px solid ${T.borderLight}` }}
        >
          <h2 style={{ fontSize: "24px", fontWeight: "bold", color: T.navy, marginBottom: "24px" }}>Manage Pricing Tiers</h2>
          <p style={{ color: T.textMuted, marginBottom: "32px", fontSize: "14px" }}>
            Manage conference ticket pricing tiers displayed on the homepage and registration page.
          </p>

          <div style={{ background: T.offWhite, padding: "24px", borderRadius: "12px", marginBottom: "32px" }}>
            <h3 style={{ fontSize: "20px", fontWeight: "semibold", color: T.navy, marginBottom: "16px" }}>
              {editingPricing ? "Edit Pricing Tier" : "Add New Pricing Tier"}
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "16px" }}>
              <div>
                <label style={{ display: "block", color: T.textMid, marginBottom: "8px", fontSize: "13px" }}>Tier Title *</label>
                <input 
                  type="text" 
                  placeholder="e.g., Student Delegate" 
                  value={newPricingTier.title} 
                  onChange={(e) => setNewPricingTier({ ...newPricingTier, title: e.target.value })} 
                  style={{ width: "100%", background: T.white, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text }} 
                />
              </div>
              <div>
                <label style={{ display: "block", color: T.textMid, marginBottom: "8px", fontSize: "13px" }}>Price (Number) *</label>
                <input 
                  type="number" 
                  placeholder="e.g., 299" 
                  value={newPricingTier.price} 
                  onChange={(e) => setNewPricingTier({ ...newPricingTier, price: parseInt(e.target.value) || 0, priceDisplay: `$${parseInt(e.target.value) || 0}` })} 
                  style={{ width: "100%", background: T.white, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text }} 
                />
              </div>
              <div>
                <label style={{ display: "block", color: T.textMid, marginBottom: "8px", fontSize: "13px" }}>Display Order</label>
                <input 
                  type="number" 
                  value={newPricingTier.order} 
                  onChange={(e) => setNewPricingTier({ ...newPricingTier, order: parseInt(e.target.value) || 0 })} 
                  style={{ width: "100%", background: T.white, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "12px", color: T.text }} 
                />
              </div>
              <div>
                <label style={{ display: "block", color: T.textMid, marginBottom: "8px", fontSize: "13px" }}>Card Color</label>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {["#06B6D4", "#3B82F6", "#F59E0B", "#10B981", "#8B5CF6", "#EF4444"].map((color) => (
                    <button 
                      key={color} 
                      type="button" 
                      onClick={() => setNewPricingTier({ ...newPricingTier, color })} 
                      style={{ width: 40, height: 40, backgroundColor: color, borderRadius: 8, border: newPricingTier.color === color ? "2px solid white" : "2px solid transparent", outline: newPricingTier.color === color ? `2px solid ${T.navy}` : "none", cursor: "pointer" }} 
                    />
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                  <input 
                    type="checkbox" 
                    checked={newPricingTier.featured} 
                    onChange={(e) => setNewPricingTier({ ...newPricingTier, featured: e.target.checked })} 
                    style={{ width: "18px", height: "18px", accentColor: T.accentGold }} 
                  />
                  <span style={{ color: T.textMid }}>⭐ Featured</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                  <input 
                    type="checkbox" 
                    checked={newPricingTier.isActive} 
                    onChange={(e) => setNewPricingTier({ ...newPricingTier, isActive: e.target.checked })} 
                    style={{ width: "18px", height: "18px", accentColor: T.primary }} 
                  />
                  <span style={{ color: T.textMid }}>✅ Active</span>
                </label>
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <label style={{ display: "block", color: T.textMid, marginBottom: "8px", fontSize: "13px" }}>Features *</label>
                <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                  <input 
                    type="text" 
                    value={featureInput} 
                    onChange={(e) => setFeatureInput(e.target.value)} 
                    onKeyPress={(e) => e.key === "Enter" && addFeatureToPricing()} 
                    placeholder="Enter a feature" 
                    style={{ flex: 1, background: T.white, border: `1px solid ${T.border}`, borderRadius: "8px", padding: "10px", color: T.text }} 
                  />
                  <button 
                    onClick={addFeatureToPricing} 
                    style={{ padding: "8px 16px", background: T.softBlue, color: T.primary, border: "none", borderRadius: "8px", cursor: "pointer" }}
                  >
                    <RiAddLine size={20} />
                  </button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {newPricingTier.features.map((f, idx) => (
                    <span key={idx} style={{ background: T.white, padding: "4px 12px", borderRadius: "999px", fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" }}>
                      <RiCheckLine size={14} style={{ color: "#4CAF50" }} /> {f}
                      <button 
                        onClick={() => removeFeatureFromPricing(idx)} 
                        style={{ background: "none", border: "none", color: "#EF4444", cursor: "pointer", fontSize: "16px" }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
              <button 
                onClick={savePricingTier} 
                disabled={loadingPricing} 
                style={{ padding: "10px 24px", background: T.gradPrimary, color: T.white, border: "none", borderRadius: "8px", display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontWeight: 600 }}
              >
                <RiSaveLine size={18} /> {loadingPricing ? "Saving..." : editingPricing ? "Update Tier" : "Create Tier"}
              </button>
              {editingPricing && (
                <button 
                  onClick={() => { 
                    setEditingPricing(null); 
                    setNewPricingTier({ title: "", price: 0, priceDisplay: "", featured: false, color: "#06B6D4", features: [], order: pricingTiers.length, isActive: true }); 
                    setFeatureInput(""); 
                  }} 
                  style={{ padding: "10px 24px", background: "#E5E7EB", color: T.textMid, border: "none", borderRadius: "8px", cursor: "pointer" }}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>

          <h3 style={{ fontSize: "20px", fontWeight: "semibold", color: T.navy, marginBottom: "16px" }}>Current Pricing Tiers</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))", gap: "16px" }}>
            {pricingTiers.sort((a, b) => a.order - b.order).map((tier) => (
              <div key={tier._id} style={{ background: T.offWhite, padding: "16px", borderRadius: "12px", borderLeft: `4px solid ${tier.featured ? T.accentGold : T.primary}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <h4 style={{ color: T.text, fontWeight: "semibold", fontSize: "18px" }}>{tier.title}</h4>
                      {tier.featured && <span style={{ fontSize: "11px", background: "#FEF3C7", color: T.accentGold, padding: "2px 8px", borderRadius: "999px" }}>Popular</span>}
                      {!tier.isActive && <span style={{ fontSize: "11px", background: "#FEE2E2", color: "#EF4444", padding: "2px 8px", borderRadius: "999px" }}>Inactive</span>}
                    </div>
                    <div style={{ fontSize: "24px", fontWeight: "bold", color: T.navy, marginBottom: "8px" }}>{tier.priceDisplay || `$${tier.price}`}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      {tier.features?.slice(0, 3).map((f, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: T.textMid }}>
                          <RiCheckLine size={12} style={{ color: "#4CAF50" }} /> {f}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <button 
                      onClick={() => { setNewPricingTier(tier); setEditingPricing(tier); }} 
                      style={{ padding: "8px", background: T.softBlue, color: T.primary, border: "none", borderRadius: "6px", cursor: "pointer" }}
                    >
                      <RiEditLine />
                    </button>
                    <button 
                      onClick={() => deletePricingTier(tier._id)} 
                      style={{ padding: "8px", background: "#FEE2E2", color: "#EF4444", border: "none", borderRadius: "6px", cursor: "pointer" }}
                    >
                      <RiDeleteBinLine />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {pricingTiers.length === 0 && !loadingPricing && (
              <div style={{ textAlign: "center", padding: "32px", color: T.textMuted, gridColumn: "span 2" }}>No pricing tiers yet.</div>
            )}
          </div>
        </motion.div>
      )}

    
    </div>
  );
};

export default ContentManagement;